import { eventAlbumSelectionConstants as C } from "../actions/constants/eventAlbumSelectionConstants";
import { eventAlbumSelectionUploadConstants as uploadC } from "../actions/constants/eventAlbumSelectionUploadConstants";

export interface CuratedMediaItem {
    blobName: string;
    sasUrl: string;
    size?: number;
    contentType?: string;
    lastModified?: string;
}

export interface AlbumVersion {
    _id: string;
    albumId: string;
    version: number;
    isCurrent?: boolean;
    status?: string;
    templateId?: string;
    pageCount?: number;
    createdAt?: string | null;
    updatedAt?: string | null;
}

export interface AlbumDeliverable {
    templateId: string;
    albumVersions?: AlbumVersion[];
    templateName?: string;
    numberOfSheets?: number;
    photosRequiredCount?: number;
    dimension?: string;
    selectionId?: string | null;
    albumId?: string | null;
    itemIndex?: number;
    status?: string;
    selectionMode?: "customer" | "self";
    selectedCount?: number;
    selectedMediaRefs?: Array<{ blobName: string }>;
    reviewDueDate?: string | null;
    submittedAt?: string | null;
    finalizedAt?: string | null;
    customerAccess?: {
        accessCode?: string;
        expiresAt?: string;
        maxAccesses?: number;
        accessCount?: number;
        isActive?: boolean;
    };
    studioComment?: string;
    customerComment?: string;
    reviewHistory?: any[];
}

export interface AlbumService {
    serviceId: string;
    serviceName: string;
    deliverables: AlbumDeliverable[];
}

export interface AlbumSummary {
    totalDeliverables: number;
    totalRequired: number;
    totalSelected: number;
    pendingSelection: number;
}

export interface CuratedTemplateMedia {
    items: CuratedMediaItem[];
    total: number;
    nextContinuationToken: string | null;
    hasMore: boolean;
}

export interface EventAlbumSelectionState {
    workspaceLoading: boolean;
    workspaceError: string | null;
    loadedForEventId: string | null;

    loading: boolean;

    transitioning: boolean;
    curatedLoading: boolean;
    curatedLoadingMore: boolean;
    customerValidating: boolean;
    customerSubmitting: boolean;

    error: string | null;

    event: any;
    summary: AlbumSummary;
    services: AlbumService[];

    activeServiceId: string | null;
    activeDeliverableKey: string | null;

    curatedByTemplate: Record<string, CuratedTemplateMedia>;

    curatedItems: CuratedMediaItem[];
    curatedTotal: number;
    curatedNextToken: string | null;
    curatedHasMore: boolean;

    localSelectedBlobNames: string[];

    customerPayload: any;
    customerAccessValidated: boolean;
    customerSubmitSuccess: boolean;

    selectedBlobUrls: { url: string; thumbnails: string }[];
    selectedAdded: boolean;

    selectedList: [];
    selectedListed: boolean;

    selectedBackgroundBlobName: string[];
    selectedBackground: boolean;
}

const initialSummary: AlbumSummary = {
    totalDeliverables: 0,
    totalRequired: 0,
    totalSelected: 0,
    pendingSelection: 0,
};

const initialState: EventAlbumSelectionState = {
    workspaceLoading: false,
    workspaceError: null,
    loadedForEventId: null,

    loading: false,
    transitioning: false,
    curatedLoading: false,
    curatedLoadingMore: false,
    customerValidating: false,
    customerSubmitting: false,

    error: null,

    event: null,
    summary: initialSummary,
    services: [],

    activeServiceId: null,
    activeDeliverableKey: null,

    curatedByTemplate: {},

    curatedItems: [],
    curatedTotal: 0,
    curatedNextToken: null,
    curatedHasMore: false,

    localSelectedBlobNames: [],
    customerPayload: null,
    customerAccessValidated: false,
    customerSubmitSuccess: false,

    selectedBlobUrls: [],
    selectedAdded: false,

    selectedList: [],
    selectedListed: false,

    selectedBackgroundBlobName: [],
    selectedBackground: false,
};

const eventAlbumSelectionReducer = (
    state: EventAlbumSelectionState = initialState,
    action: { type: string; payload?: any },
): EventAlbumSelectionState => {
    switch (action.type) {
        // -- Workspace -------------------------------------------------------------
        case C.fetchWorkspaceRequest:
            return { ...state, workspaceLoading: true, workspaceError: null };
        case C.fetchWorkspaceSuccess: {
            const rawCurated = action.payload?.curatedByTemplate ?? {};
            const curatedByTemplate: Record<string, CuratedTemplateMedia> = {};
            for (const [key, data] of Object.entries(rawCurated)) {
                const d = data as any;
                curatedByTemplate[key] = {
                    items: d?.items ?? [],
                    total: d?.total ?? 0,
                    nextContinuationToken: d?.nextContinuationToken ?? null,
                    hasMore: d?.hasMore ?? false,
                };
            }
            return {
                ...state,
                workspaceLoading: false,
                workspaceError: null,
                loadedForEventId: action.payload?.event?._id ?? state.loadedForEventId,
                event: action.payload?.event ?? null,
                services: action.payload?.services ?? [],
                summary: action.payload?.summary ?? initialSummary,
                curatedByTemplate,
            };
        }
        case C.fetchWorkspaceFailure:
            return { ...state, workspaceLoading: false, workspaceError: action.payload };

        // Upload service � patch curated data for a specific template
        case uploadC.listAlbumCuratedMediaSuccess: {
            const { serviceId, templateId, itemIndex } = action.payload ?? {};
            if (!templateId) return state;
            const key = `${serviceId}_${templateId}_${itemIndex ?? 0}`;
            return {
                ...state,
                curatedByTemplate: {
                    ...state.curatedByTemplate,
                    [key]: {
                        items: action.payload?.items ?? [],
                        total: action.payload?.total ?? 0,
                        nextContinuationToken: action.payload?.nextContinuationToken ?? null,
                        hasMore: action.payload?.hasMore ?? false,
                    },
                },
            };
        }

        // -- Summary ---------------------------------------------------------------
        case C.fetchSummaryRequest:
            return { ...state, loading: true, error: null };
        case C.fetchSummarySuccess:
            return {
                ...state,
                loading: false,
                event: action.payload?.event ?? null,
                services: action.payload?.services ?? [],
                summary: action.payload?.summary ?? initialSummary,
                error: null,
            };
        case C.fetchSummaryFailure:
            return { ...state, loading: false, error: action.payload };

        case C.changeStatusRequest:
            return { ...state, transitioning: true, error: null };
        case C.changeStatusSuccess: {
            const updated = action.payload;
            if (!updated || !updated.serviceId || !updated.templateId) {
                return { ...state, transitioning: false, error: null };
            }
            const updatedServiceId = String(updated.serviceId);
            const updatedTemplateId = String(updated.templateId);
            const updatedServices = state.services.map((svc) => {
                if (String(svc.serviceId) !== updatedServiceId) return svc;
                return {
                    ...svc,
                    deliverables: svc.deliverables.map((d) => {
                        if (String(d.templateId) !== updatedTemplateId) return d;
                        return {
                            ...d,
                            selectionId: String(updated._id ?? d.selectionId),
                            status: updated.status ?? d.status,
                            reviewDueDate: updated.reviewDueDate ?? d.reviewDueDate,
                            submittedAt: updated.submittedAt ?? d.submittedAt,
                            finalizedAt: updated.finalizedAt ?? d.finalizedAt,
                            selectedCount: updated.selectedMediaRefs?.length ?? d.selectedCount,
                            studioComment: updated.comments?.studioComment ?? d.studioComment,
                            customerAccess: updated.customerAccess ?? d.customerAccess,
                            reviewHistory: updated.reviewHistory ?? d.reviewHistory,
                        };
                    }),
                };
            });
            return { ...state, transitioning: false, error: null, services: updatedServices };
        }
        case C.changeStatusFailure:
            return { ...state, transitioning: false, error: action.payload };

        // -- Curated Media ---------------------------------------------------------
        case C.listCuratedRequest:
            return { ...state, curatedLoading: true, curatedItems: [], error: null };
        case C.listCuratedSuccess:
            return {
                ...state,
                curatedLoading: false,
                curatedItems: action.payload?.items ?? [],
                curatedTotal: action.payload?.total ?? 0,
                curatedNextToken: action.payload?.nextContinuationToken ?? null,
                curatedHasMore: action.payload?.hasMore ?? false,
                error: null,
            };
        case C.listCuratedFailure:
            return { ...state, curatedLoading: false, error: action.payload };

        case C.listCuratedMore:
            return { ...state, curatedLoadingMore: true, error: null };
        case C.listCuratedMoreSuccess:
            return {
                ...state,
                curatedLoadingMore: false,
                curatedItems: [...state.curatedItems, ...(action.payload?.items ?? [])],
                curatedTotal: action.payload?.total ?? state.curatedTotal,
                curatedNextToken: action.payload?.nextContinuationToken ?? null,
                curatedHasMore: action.payload?.hasMore ?? false,
                error: null,
            };

        // -- Customer --------------------------------------------------------------
        case C.customerValidateRequest:
            return {
                ...state,
                customerValidating: true,
                error: null,
                customerAccessValidated: false,
            };
        case C.customerValidateSuccess:
            return {
                ...state,
                customerValidating: false,
                customerPayload: action.payload,
                customerAccessValidated: true,
                error: null,
            };
        case C.customerValidateFailure:
            return {
                ...state,
                customerValidating: false,
                error: action.payload,
                customerAccessValidated: false,
            };

        case C.customerSubmitRequest:
            return {
                ...state,
                customerSubmitting: true,
                error: null,
                customerSubmitSuccess: false,
            };
        case C.customerSubmitSuccess:
            return {
                ...state,
                customerSubmitting: false,
                customerSubmitSuccess: true,
                error: null,
            };
        case C.customerSubmitFailure:
            return {
                ...state,
                customerSubmitting: false,
                error: action.payload,
                customerSubmitSuccess: false,
            };

        // -- UI State --------------------------------------------------------------
        case C.setActiveService:
            return { ...state, activeServiceId: action.payload, activeDeliverableKey: null };
        case C.setActiveDeliverable:
            return { ...state, activeDeliverableKey: action.payload };
        case C.updateLocalSelections:
            return { ...state, localSelectedBlobNames: action.payload };

        case C.reset:
            return { ...initialState };
        case C.resetCustomer:
            return {
                ...state,
                customerPayload: null,
                customerAccessValidated: false,
                customerSubmitSuccess: false,
                localSelectedBlobNames: [],
                error: null,
            };

        case C.restoreCustomerSession:
            return {
                ...state,
                customerValidating: false,
                customerPayload: action.payload.customerPayload,
                customerAccessValidated: true,
                localSelectedBlobNames: action.payload.localSelections ?? [],
                error: null,
            };

        case C.addToSelectedRequest:
            return {
                ...state,
                error: null,
                loading: true,
                selectedAdded: false,
            };

        case C.addToBackgroundRequest:
            return {
                ...state,
                error: null,
                loading: true,
                selectedBackground: false,
            };

        case C.listSelectedRequest:
            return {
                ...state,
                error: null,
                loading: true,
                selectedListed:false,
            };
        case C.addToSelectedSuccess:
            return {
                ...state,
                selectedBlobUrls: action.payload,
                error: null,
                loading: false,
                selectedAdded: true,
            };
        case C.addToBackgroundSuccess:
             return {
                ...state,
                selectedBackgroundBlobName: action.payload,
                error: null,
                loading: false,
                selectedBackground: true,
            };
        case C.listSelectedSuccess:
            return {
                ...state,
                selectedList: action.payload,
                error: null,
                loading: false,
                selectedListed: true,
            };

        case C.addToSelectedFailure:
            return {
                ...state,
                error: action.payload,
                loading: false,
                selectedAdded: false,
            };
        case C.addToBackgroundFailure:
            return {
                ...state,
                error: action.payload,
                loading: false,
                selectedBackground: false,
            };
        case C.listSelectedFailure:
            return {
                ...state,
                error: action.payload,
                loading: false,
                selectedListed: false,
            };
        default:
            return state;
    }
};

// -- Selectors -----------------------------------------------------------------

export const selectAlbumWorkspaceLoading = (state: any): boolean =>
    state.albumSelection?.workspaceLoading ?? false;
export const selectAlbumWorkspaceError = (state: any): string | null =>
    state.albumSelection?.workspaceError ?? null;
export const selectAlbumTransitioning = (state: any): boolean =>
    state.albumSelection?.transitioning ?? false;
export const selectAlbumLoading = (state: any): boolean => state.albumSelection?.loading ?? false;
export const selectAlbumEvent = (state: any) => state.albumSelection?.event ?? null;
export const selectAlbumServices = (state: any): AlbumService[] =>
    state.albumSelection?.services ?? [];
export const selectAlbumSummary = (state: any): AlbumSummary =>
    state.albumSelection?.summary ?? initialSummary;
export const selectAlbumActiveServiceId = (state: any): string | null =>
    state.albumSelection?.activeServiceId ?? null;
export const selectAlbumActiveDeliverableKey = (state: any): string | null =>
    state.albumSelection?.activeDeliverableKey ?? null;

export const selectAlbumActiveDeliverable = (state: any): AlbumDeliverable | null => {
    const key: string | null = state.albumSelection?.activeDeliverableKey;
    if (!key) return null;
    const [serviceId, templateId] = key.split("::");
    const service: AlbumService | undefined = (state.albumSelection?.services ?? []).find(
        (s: AlbumService) => s.serviceId === serviceId,
    );
    return service?.deliverables.find((d) => d.templateId === templateId) ?? null;
};

export const selectCuratedByTemplate = (state: any, templateId: string): CuratedTemplateMedia =>
    state.albumSelection?.curatedByTemplate?.[templateId] ?? {
        items: [],
        total: 0,
        nextContinuationToken: null,
        hasMore: false,
    };

export const selectCuratedMedia = (state: any): CuratedMediaItem[] =>
    state.albumSelection?.curatedItems ?? [];
export const selectCuratedHasMore = (state: any): boolean =>
    state.albumSelection?.curatedHasMore ?? false;
export const selectCuratedNextToken = (state: any): string | null =>
    state.albumSelection?.curatedNextToken ?? null;
export const selectLocalSelections = (state: any): string[] =>
    state.albumSelection?.localSelectedBlobNames ?? [];
export const selectCustomerPayload = (state: any) => state.albumSelection?.customerPayload ?? null;
export const selectCustomerAccessValidated = (state: any): boolean =>
    state.albumSelection?.customerAccessValidated ?? false;
export const selectCustomerSubmitSuccess = (state: any): boolean =>
    state.albumSelection?.customerSubmitSuccess ?? false;

export default eventAlbumSelectionReducer;
